/**
 * The capture queue: a typed thought is written to IndexedDB first, then
 * uploaded to the coop's `pages/` folder. If the upload fails (offline, 5xx) the
 * draft stays queued and `flush()` retries it — on reconnect and on app focus.
 *
 * `clientId` rides along to the backend so a retried upload is idempotent.
 */
import { ApiError } from './api';
import { allEggs, deleteEgg, saveEggDraft } from './cache';
import { storageEgg } from './storage';
import { newId } from './id';
import { buildEgg } from './egg';
import type { EggDraft } from '../shared/coop-types.js';

export type EggStatus = 'uploading' | 'queued' | 'error';

export interface QueuedEgg extends EggDraft {
  status: EggStatus;
  error?: string;
}

class Capture {
  queue = $state<QueuedEgg[]>([]);
  loaded = $state(false);

  get pending(): number {
    return this.queue.length;
  }

  async hydrate(): Promise<void> {
    const drafts = await allEggs().catch(() => [] as EggDraft[]);
    this.queue = drafts.map((d) => ({ ...d, status: 'queued' as const }));
    this.loaded = true;
    void this.flush();
  }

  /** Write a capture. Resolves once it's durably queued (upload runs in the background). */
  async save(text: string): Promise<void> {
    if (!text.trim()) return;
    const egg = buildEgg(text);
    const draft: EggDraft = {
      clientId: newId(),
      slug: egg.slug,
      markdown: egg.content,
      createdAt: new Date().toISOString(),
    };
    await saveEggDraft(draft);
    this.queue = [...this.queue, { ...draft, status: 'uploading' }];
    void this.#upload(draft.clientId);
  }

  /** Retry every draft that isn't already in flight. */
  async flush(): Promise<void> {
    for (const item of this.queue) {
      if (item.status !== 'uploading') await this.#upload(item.clientId);
    }
  }

  async #upload(clientId: string): Promise<void> {
    const item = this.queue.find((q) => q.clientId === clientId);
    if (!item) return;
    this.#patch(clientId, { status: 'uploading', error: undefined });
    try {
      await storageEgg(item.slug, item.markdown, item.clientId);
      await deleteEgg(clientId);
      this.queue = this.queue.filter((q) => q.clientId !== clientId);
    } catch (err) {
      const offline = err instanceof ApiError && err.status === 0;
      this.#patch(clientId, {
        status: offline ? 'queued' : 'error',
        error: err instanceof ApiError ? err.message : 'Upload failed.',
      });
    }
  }

  #patch(clientId: string, fields: Partial<QueuedEgg>): void {
    this.queue = this.queue.map((q) => (q.clientId === clientId ? { ...q, ...fields } : q));
  }
}

export const capture = new Capture();
